import { Button, Form, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

interface SupplierOption {
  id: number | string;
  name: string;
}

interface WarehouseFormData {
  name: string;
  location: string;
  description?: string;
  supplier?: number | string | null;
}

interface WarehouseCreateModalProps {
  show: boolean;
  formData: WarehouseFormData;
  submitting: boolean;
  suppliers?: SupplierOption[];
  mode?: 'create' | 'edit';
  onClose: () => void;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  onFieldChange: (field: keyof WarehouseFormData, value: string) => void;
}

const WarehouseCreateModal = ({
  show,
  formData,
  submitting,
  suppliers = [],
  mode = 'create',
  onClose,
  onSubmit,
  onFieldChange,
}: WarehouseCreateModalProps) => {
  const { t } = useTranslation();
  const isEditMode = mode === 'edit';

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>{isEditMode ? t('warehouses.inventory.edit') : t('warehouses.newWarehouse')}</Modal.Title>
      </Modal.Header>
      <form onSubmit={onSubmit}>
        <Modal.Body>
          <div className="mb-3">
            <label className="form-label" htmlFor="warehouse-name">{t('warehouses.name')}</label>
            <Form.Control
              id="warehouse-name"
              type="text"
              value={formData.name}
              onChange={(event) => onFieldChange('name', event.target.value)}
              maxLength={100}
              required
              autoFocus={isEditMode}
            />
          </div>
          <div className="mb-3">
            <label className="form-label" htmlFor="warehouse-location">{t('warehouses.location')}</label>
            <Form.Control
              id="warehouse-location"
              type="text"
              value={formData.location}
              onChange={(event) => onFieldChange('location', event.target.value)}
              maxLength={255}
              required={isEditMode}
            />
          </div>
          <div className="mb-3">
            <label className="form-label" htmlFor="warehouse-description">{t('warehouses.inventory.description')}</label>
            <Form.Control
              id="warehouse-description"
              as="textarea"
              rows={4}
              value={formData.description ?? ''}
              onChange={(event) => onFieldChange('description', event.target.value)}
            />
          </div>
          <div className="mb-3">
            <label className="form-label" htmlFor="warehouse-supplier">{t('warehouses.supplier', 'Dodavatel (externí sklad)')}</label>
            <Form.Select
              id="warehouse-supplier"
              value={formData.supplier ?? ''}
              onChange={(event) => onFieldChange('supplier', event.target.value)}
            >
              <option value="">{t('warehouses.ownWarehouse', 'Vlastní sklad')}</option>
              {suppliers.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </Form.Select>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" type="submit" disabled={submitting}>
            {submitting ? t('common.saving', 'Saving…') : isEditMode ? t('common.save') : t('common.create')}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export default WarehouseCreateModal;
